import { Avatar, Box, Grid, Tooltip, Typography } from '@material-ui/core';
import { Theme, makeStyles, useTheme, withStyles } from '@material-ui/core/styles';
import { getNameInCamelCase, Roles } from '@roc/feature-utils';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useCallPresence, useUserStore, useWebSocketStore } from '../../hooks';
import { CALL_PRESENCE_LABEL } from '../../stores/meetPresenceStore';
import { Chat, Headset, Person, OpenInNew, Add } from '@material-ui/icons';
import { Button, ConditionalWrapper } from '@roc/ui';

const useStyles = makeStyles(theme => ({
  statusIndicator: {
    width: 14,
    height: 14,
    marginRight: 6,
    borderRadius: 4,
  },
  onlineStatusIndicator: {
    background: 'radial-gradient(circle, #0ce90c, #009500)'
  },
  offlineStatusIndicator: {
    background: 'radial-gradient(circle, #fff, #bbb)'
  },
}));

const DisplayNameTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const DisplayNameTooltipContent = observer(({ displayName, userId, isUserActive, allowHuddle, onUserNewConversation, onHuddle, calendlyLink, roleName, isInternalUser, onOpenAddTask }) => {
  const { userStore } = useUserStore();
  // The card opens on hover over one name, so this is also where the answer is fetched.
  const { presenceOf } = useCallPresence(!!userId);
  const isMessageSenderBackOfficeOrInternal = isInternalUser || [Roles.BACK_OFFICE_LEAD, Roles.BACK_OFFICE_ORIGINATOR, Roles.BACK_OFFICE_TEAM_LEAD, Roles.BACK_OFFICE_PROCESSOR].includes(roleName);
  const isLoggedInUserInternalOrBackOffice = userStore.isBackOfficeUser || userStore.isNportalUser;

  /*
   * Huddle rings the other side, and the online dot beside the name does not say whether anyone
   * is there to hear it: it means "signed in", while an invite is only ever raised in the LoanChat
   * desktop app. A colleague reading chat in a browser shows as online and cannot be called, so
   * offering Huddle on that signal produced a call that rang into nothing for a minute.
   *
   * Nor does signed-in-and-callable mean free. Someone already on a call has the invite answered
   * BUSY by their own client the instant it arrives, so Huddle would open a window that closed
   * itself. useCallPresence joins the two facts, and this card, the participant list, Meet's own
   * directory and the mobile app all read the one answer.
   */
  const showHuddle = allowHuddle && onHuddle && !!userId;
  const presence = presenceOf(userId);
  const canHuddle = presence === 'available';

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box display={'flex'} p={2} alignItems={'center'}>
          <Box>
            <Avatar variant="square">
              <Person fontSize={'large'} style={{ color: '#333' }} />
            </Avatar>
          </Box>
          <Box flexGrow={1} pl={1}>{displayName}</Box>
        </Box>
      </Grid>
      {!isUserActive ? <></> :
        <>
          <Grid item xs={12}>
            <Box borderBottom={'1px solid #eee'}></Box>
          </Grid>
          <Grid item xs={12}>
            <Box display={'flex'} p={2} alignItems={'center'} flexWrap="wrap">
              {calendlyLink && (
                <Box mr={1} mb={1}>
                  <Button
                    color='primary'
                    variant='outlined'
                    testId='calendly-link'
                    size="medium"
                    endIcon={<OpenInNew style={{ color: '#2196f3' }} />}
                    onClick={() => {
                      if ((window as any).require) {
                        const { shell } = (window as any).require('electron');
                        shell?.openExternal(calendlyLink);
                      } else {
                        window.open(calendlyLink);
                      }
                    }}
                  >
                    Calendly Link
                  </Button>
                </Box>
              )}
              {onUserNewConversation && userId && (
                <Box mr={1} mb={1}>
                  <Button
                    color='primary'
                    variant='outlined'
                    testId='new-conversation'
                    size="medium"
                    onClick={() => {
                      onUserNewConversation(userId);
                    }}
                    startIcon={<Chat fontSize="medium" />}
                  >
                    New Conversation
                  </Button>
                </Box>
              )}
              {showHuddle && (
                <Box mb={1} display="flex" alignItems="center">
                  {/* A disabled MUI button fires no pointer events, so the tooltip needs a live
                      wrapper to hang off — otherwise the one case that needs explaining is the
                      one case that cannot explain itself. */}
                  <Tooltip
                    title={canHuddle
                      ? ''
                      : presence === 'on-another-call'
                        ? `${displayName} is already on another call.`
                        : `${displayName} isn't available for calls right now.`}
                    placement="top"
                  >
                    <span>
                      <Button
                        color='primary'
                        variant='outlined'
                        testId='huddle'
                        size="medium"
                        disabled={!canHuddle}
                        onClick={() => {
                          onHuddle([userId]);
                        }}
                        startIcon={<Headset fontSize="medium" />}
                      >
                        Huddle
                      </Button>
                    </span>
                  </Tooltip>
                  {/* Beside the button, so the card answers "can I get them" before it is pressed. */}
                  <Box
                    ml={1}
                    fontSize={12}
                    color={canHuddle ? 'success.main' : 'text.secondary'}
                    whiteSpace="nowrap"
                  >
                    {CALL_PRESENCE_LABEL[presence]}
                  </Box>
                </Box>
              )}
              {(onOpenAddTask && isMessageSenderBackOfficeOrInternal && isLoggedInUserInternalOrBackOffice) && (
                <Box mb={1}>
                  <Button
                    color='primary'
                    variant='outlined'
                    testId='createTask'
                    size="medium"
                    onClick={() => onOpenAddTask(userId)}
                    startIcon={<Add fontSize="medium" />}
                  >
                    Create Task
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </>
      }
    </Grid>
  );
});


export const DisplayName = ({ displayName, firstName, lastName, isUserActive }) => {
  const nameToDisplay = displayName || getNameInCamelCase(firstName, lastName);
  const theme = useTheme();
  const color = !isUserActive ? theme.palette.error.main : undefined;
  return (
    <>
      <Box style={{ color }}>
        {nameToDisplay}
      </Box>
      {(!isUserActive) &&
        <Typography style={{ color: theme.palette.error.main, marginLeft: '10px' }}>{'[Deactivated]'}</Typography>
      }
    </>
  );
}

const StatusIndicator = ({ isOnline }) => {
  const classes = useStyles();
  return (
    <Tooltip title={isOnline ? 'Online' : 'Offline'} placement='top'>
      <Box className={clsx(classes.statusIndicator, isOnline ? classes.onlineStatusIndicator : classes.offlineStatusIndicator)}></Box>
    </Tooltip>
  );
}

export const DisplayNameWithOnlineStatus = observer(({ userId,
  displayName = undefined,
  firstName = undefined,
  lastName = undefined,
  isActive = undefined,
  allowHuddle = false,
  onHuddle = undefined,
  onUserNewConversation = undefined,
  onOpenAddTask = undefined,
  showHoverPopup = false,
  participantCalendlyLink = undefined,
  roleName = undefined,
  isInternalUser = false,
}: {
  userId: number;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  allowHuddle?: boolean;
  onHuddle?: (inviteUserIds: number[]) => void;
  onUserNewConversation?: (inviteUserId: number) => void;
  onOpenAddTask?: (userId: number) => void;
  showHoverPopup?: boolean;
  participantCalendlyLink?: string,
  roleName?: string,
  isInternalUser?: boolean,
}) => {
  const { webSocketStore } = useWebSocketStore();
  const onlineUsers = webSocketStore?.onlineUsers;
  const isOnline = (onlineUsers && userId) ? onlineUsers[userId] : false;

  return (
    <>
      {
        onlineUsers == undefined ? null :
          <Box display="flex">
            {<StatusIndicator isOnline={isOnline} />}
          </Box>
      }
      <ConditionalWrapper condition={showHoverPopup} wrapper={(children) => {
        return (<DisplayNameTooltip
          interactive
          placement='top'
          title={<DisplayNameTooltipContent
            displayName={displayName}
            userId={userId}
            allowHuddle={allowHuddle}
            onUserNewConversation={onUserNewConversation}
            onHuddle={onHuddle}
            onOpenAddTask={onOpenAddTask}
            isUserActive={isActive}
            calendlyLink={participantCalendlyLink}
            roleName={roleName}
            isInternalUser={isInternalUser}
          />}>
          <div style={{
            cursor: 'pointer'
          }}>
            {children}
          </div>
        </DisplayNameTooltip>)
      }}>
        <DisplayName
          displayName={displayName}
          firstName={firstName}
          lastName={lastName}
          isUserActive={isActive} />
      </ConditionalWrapper>
    </>
  )
});